import React from 'react';
import { alpha, ListItem } from '@mui/material';
import { ReactComponent as UserSwitch } from '../../shared/icons/UserSwitch.svg';
import { ReactComponent as Processor } from '../../shared/icons/Processor.svg';
import { ReactComponent as Producer } from '../../shared/icons/Producer.svg';
import { ReactComponent as Hauler } from '../../shared/icons/Hauling.svg';
import COMMON_STATUS_CHIP_CONFIG from '../../../../../custom-components/custom-chip/common_chip_styles';
import LiquidChip from '../../../../../custom-components/custom-chip/LiquidChip';

export const getUserIcon = (isImpersonating, role) => {
    if (!isImpersonating) return <UserSwitch />;

    switch (role) {
        case 'processor':
            return <Processor />;
        case 'producer':
            return <Producer />;
        case 'transport':
            return <Hauler />;
        default:
            return <UserSwitch />;
    }
};

export const getRecentItems = (key) => {
    try {
        const parsed = JSON.parse(localStorage.getItem(key));
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const getPath = (item) => (typeof item === 'string' ? item : item?.path);

export const addRecentItem = (key, item, max = 5) => {
    const current = getRecentItems(key);
    const itemPath = getPath(item);
    const next = [item, ...current.filter((i) => getPath(i) !== itemPath)].slice(0, max);
    localStorage.setItem(key, JSON.stringify(next));
};

const LiquidChipConfig = {
    processor: COMMON_STATUS_CHIP_CONFIG.error.faded,
    producer: COMMON_STATUS_CHIP_CONFIG.success.faded,
    transport: COMMON_STATUS_CHIP_CONFIG.warning.faded,
    main: COMMON_STATUS_CHIP_CONFIG.info.faded,
};

const getOptionLabel = (type, localization) => {
    const labelMap = {
        processor: localization.general.processor_singular ?? 'Processor',
        producer: 'Producer',
        transport: localization.general.hauling_singular ?? 'Hauler',
        main: 'Main',
    };

    return labelMap[type]?.toUpperCase();
};

/**
 * Get the main user option for the switch user dropdown.
 * If impersonating, retrieves stored main user info from sessionStorage.
 * If not impersonating, creates option from current user.
 * For sub-producers/processors/haulers, uses the parent user's name.
 * Sub-admins use their own name.
 * @param {Object} currentUser - The current user object
 * @param {boolean} isImpersonating - Whether currently impersonating another user
 * @param {string|null} parentName - The pre-fetched parent user's name (for sub-users)
 * @returns {Object|null} The main user option or null if not available
 */
export const getMainUserOption = (currentUser, isImpersonating, parentName = null) => {
    if (isImpersonating) {
        const mainUserId = sessionStorage.getItem('main_user_id');
        const mainUserName = sessionStorage.getItem('main_user_name');

        if (mainUserId && mainUserName) {
            return {
                id: mainUserId,
                label: mainUserName,
                type: 'main',
                isMainUser: true,
            };
        }
        return null;
    }

    if (currentUser?.id && currentUser?.name) {
        // For sub-producers/processors/haulers, use parent's name if available
        // Sub-admins use their own name
        const hasParent = currentUser.subuser && (currentUser.producer_id || currentUser.processor_id || currentUser.hauling_id);
        const displayName = hasParent && parentName ? parentName : currentUser.name;

        return {
            id: currentUser.id,
            label: displayName,
            type: 'main',
            isMainUser: true,
        };
    }
    return null;
};

export const renderOptions = (options, onClick, localization, currentUserId = null) =>
    options.map((option) => {
        const isSelected = option.id === currentUserId;
        const licenseNumber = option.license_number ? ` - ${option.license_number}` : '';
        const haulerNumber = option.hauler_number ? ` - ${option.hauler_number}` : '';
        const extraInfo = option.type === 'transport' ? haulerNumber : licenseNumber;
        return (
            <ListItem
                key={option.id}
                onClick={() => onClick(option)}
                sx={(theme) => ({
                    '&:hover': {
                        cursor: 'pointer',
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                    ...(option.status === 'Inactive' && {
                        color: theme.palette.nav.text.med,
                    }),
                    ...(isSelected && {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        borderLeft: `3px solid ${theme.palette.primary.main}`,
                    }),
                })}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '16px',
                        width: '100%',
                    }}
                >
                    <span style={{ fontWeight: isSelected ? 600 : 400 }}>{`${option.label}${extraInfo}`}</span>
                    <LiquidChip
                        label={getOptionLabel(option.type, localization)}
                        {...LiquidChipConfig[option.type]}
                        round={false}
                        sx={{
                            height: '24px',
                            fontSize: '12px',
                            fontWeight: 600,
                            letterSpacing: '1px',
                        }}
                    />
                </div>
            </ListItem>
        );
    });
